import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { useQuery } from "@tanstack/react-query";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import {
  useItemAllowances,
  useItemMargins,
  useItemPromoPrices,
  useItemScans,
  useUpdateItemPromoPrice,
  type PromoPriceWriteRequest,
} from "src/dm/hooks/useDealItemSub";
import { useDealItems } from "src/dm/hooks/useDealItems";
import { fetchRetailItemByNumber } from "src/dm/hooks/useRetailItems";
import { DealItem } from "src/dm/interfaces/DealItem";
import { RetailItem } from "src/dm/interfaces/RetailItem";
import { formatCostZoneStore, formatZoneOverrideStore } from "src/dm/utils/zoneFormat";
import { DealItemAllowance, DealItemMargin, DealItemPromoPrice, DealItemScan } from "src/dm/interfaces/DealItemSub";

const useDealItem = (dealId: number | undefined, dealItemId: number | undefined) =>
  useQuery({
    queryKey: ["deal-item", dealId, dealItemId],
    enabled:
      dealId !== undefined && dealId > 0 &&
      dealItemId !== undefined && dealItemId > 0,
    queryFn: async (): Promise<DealItem> => {
      const response = await apiClient.get<DealItem>(
        `${APIS.Deals}/${dealId}/items/${dealItemId}`
      );
      return response.data;
    },
  });

// React Query for the joined retail-item lookup. We key it off the
// RITEM_NO so it's cached across detail pages for the same item.
const useRetailItemDetails = (retailItemNumber: number | undefined) =>
  useQuery({
    queryKey: ["retail-item", retailItemNumber],
    enabled: retailItemNumber !== undefined && retailItemNumber > 0,
    queryFn: async (): Promise<RetailItem | null> =>
      fetchRetailItemByNumber(retailItemNumber as number),
  });

const fmtDate = (s?: string | null) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

const SummaryField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <div style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </div>
    <div style={{ fontSize: "0.92rem", color: "#0f172a" }}>{value ?? "—"}</div>
  </div>
);

// Mapping from URL `?tab=…` slugs to TabStrip tab indexes. Slugs are kept
// lowercase-hyphen so the URL is shareable and survives copy/paste; legacy
// ViewItem.aspx had no equivalent (it only had one big form), so the slugs
// are a SPA addition. audit/26-gap-closure-verification.md §3 item 8.
//   0 Summary, 1 Allowances, 2 Scans, 3 Promo Prices, 4 Margins
const TAB_SLUG_TO_INDEX: Record<string, number> = {
  summary: 0,
  allowances: 1,
  scans: 2,
  "promo-prices": 3,
  margins: 4,
};

const DealItemDetailPage = () => {
  const deal = useDealContext();
  const navigate = useNavigate();
  const { dealItemId: dealItemIdParam } = useParams<{ dealItemId: string }>();
  const dealItemId = dealItemIdParam ? Number(dealItemIdParam) : undefined;
  const [searchParams] = useSearchParams();
  // Default tab from the URL slug. Falls back to 0 (Summary) when the
  // param is missing or unrecognised — preserves the prior default
  // behaviour for direct deep-links that don't carry a `?tab=`.
  const initialTab = (() => {
    const slug = (searchParams.get("tab") ?? "").toLowerCase();
    return TAB_SLUG_TO_INDEX[slug] ?? 0;
  })();
  const [tab, setTab] = useState(initialTab);

  const item = useDealItem(deal.dealId, dealItemId);
  const allItems = useDealItems(deal.dealId);
  const retail = useRetailItemDetails(item.data?.retailItemNumber);
  const allowances = useItemAllowances(deal.dealId, dealItemId);
  const scans = useItemScans(deal.dealId, dealItemId);
  const promoPrices = useItemPromoPrices(deal.dealId, dealItemId);
  const margins = useItemMargins(deal.dealId, dealItemId);

  // Recalc-margins call (stub endpoint — currently returns 501). We don't
  // use react-query mutation here because the result is informational only.
  const [recalcMsg, setRecalcMsg] = useState<string>("");
  const [recalcPending, setRecalcPending] = useState(false);

  // ---------------------------------------------------------------------
  // Promo-price inline edit state. Legacy ViewItem.aspx had a separate
  // Edit popup; the new SPA opts for an inline pattern (the row's number
  // cells become editable NumericTextBoxes when the user clicks "Edit").
  // `editPromoPriceId` holds the DIPP_ID currently being edited, plus a
  // local copy of the editable fields (the price, amount-off, and
  // percent-off values — the only three legacy ViewItem.aspx wrote).
  // ---------------------------------------------------------------------
  const [editPromoPriceId, setEditPromoPriceId] = useState<number | null>(null);
  const [editPromoForm, setEditPromoForm] = useState<{
    price: number | null;
    amountOff: number | null;
    percentOff: number | null;
    multiUnitCount: number | null;
  }>({ price: null, amountOff: null, percentOff: null, multiUnitCount: null });
  const [editPromoError, setEditPromoError] = useState<string | null>(null);
  const updatePromoPrice = useUpdateItemPromoPrice(deal.dealId, dealItemId);

  // Compute previous / next dealItemId from the full items list (legacy
  // BuildBackForwardFunction). Items are ordered by DITM_ID server-side.
  const { prevId, nextId } = useMemo(() => {
    const list = allItems.data ?? [];
    const idx = list.findIndex((i) => i.dealItemId === dealItemId);
    if (idx < 0) return { prevId: undefined, nextId: undefined };
    return {
      prevId: idx > 0 ? list[idx - 1].dealItemId : undefined,
      nextId: idx < list.length - 1 ? list[idx + 1].dealItemId : undefined,
    };
  }, [allItems.data, dealItemId]);

  // Reset the recalc message any time we navigate to a different item.
  useEffect(() => {
    setRecalcMsg("");
    setEditPromoPriceId(null);
    setEditPromoError(null);
  }, [dealItemId]);

  /**
   * Open the inline editor for one promo-price row. Pulls the current
   * values into the local form so the user can change them. Click "Save"
   * to PUT, "Cancel" to revert.
   */
  const beginEditPromoPrice = (row: DealItemPromoPrice) => {
    setEditPromoPriceId(row.promoPriceId);
    setEditPromoForm({
      price: row.price,
      amountOff: row.amountOff,
      percentOff: row.percentOff,
      multiUnitCount: row.multiUnitCount,
    });
    setEditPromoError(null);
  };

  const cancelEditPromoPrice = () => {
    setEditPromoPriceId(null);
    setEditPromoError(null);
  };

  /**
   * Save the inline-edit form to the API. Builds the full write request
   * from the original row + the form overrides, so columns we don't
   * surface in the editor (zone, override, store, special pricing, etc.)
   * round-trip unchanged. Mirrors legacy ViewItem.aspx promo-price save
   * which kept the original ZN_NO / OVRD_NO / STR_NO and only mutated
   * the dollar fields.
   *
   * Oracle trigger DIPP_BEF_IU enforces that EXACTLY ONE of:
   *   - (PRC + MUNIT_CNT) - a multi-unit price
   *   - AMT_OFF           - a dollar amount off
   *   - PCT_OFF           - a percent off
   *   - SPCL_PRICING + USE_CURR_PRC_FG - a special pricing flag
   * be populated per row. We auto-clear the other slots based on what the
   * user typed so a save with AmountOff != null doesn't violate the rule.
   */
  const saveEditPromoPrice = async (original: DealItemPromoPrice) => {
    const { price, amountOff, percentOff, multiUnitCount } = editPromoForm;
    // Decide which "mode" the user is saving in. Priority: amountOff > percentOff > price.
    // Validate exactly one of (price/amount/percent) is set.
    const setSlots = [
      amountOff != null && amountOff !== 0 ? "amountOff" : null,
      percentOff != null && percentOff !== 0 ? "percentOff" : null,
      price != null && price !== 0 ? "price" : null,
    ].filter(Boolean);
    if (setSlots.length === 0) {
      setEditPromoError(
        "Set at least one of Price, Amount Off, or % Off (Oracle trigger DIPP_BEF_IU requires exactly one).",
      );
      return;
    }
    if (setSlots.length > 1) {
      setEditPromoError(
        `Set exactly one of Price, Amount Off, or % Off — got ${setSlots.join(", ")}. ` +
          "Clear the other two before saving.",
      );
      return;
    }
    const body: PromoPriceWriteRequest = {
      zoneNumber: original.zoneNumber,
      overrideNumber: original.overrideNumber,
      storeNumber: original.storeNumber,
      // multi-unit count is only relevant when price is set; otherwise null
      multiUnitCount: setSlots[0] === "price" ? (multiUnitCount ?? 1) : null,
      price: setSlots[0] === "price" ? price : null,
      amountOff: setSlots[0] === "amountOff" ? amountOff : null,
      percentOff: setSlots[0] === "percentOff" ? percentOff : null,
      specialPricing: original.specialPricing,
      promoGroupId: null,
      useCurrentPrice: original.useCurrentPrice,
      regularRetailMarginBeforeScan: original.regularRetailMarginBeforeScan,
      promoRetailMarginBeforeScan: original.promoRetailMarginBeforeScan,
      promoRetailMarginAfterScan: original.promoRetailMarginAfterScan,
    };
    try {
      await updatePromoPrice.mutateAsync({
        promoPriceId: original.promoPriceId,
        body,
      });
      setEditPromoPriceId(null);
      setEditPromoError(null);
    } catch (err) {
      const anyErr = err as { response?: { data?: unknown }; message?: string };
      const errBody =
        anyErr.response?.data !== undefined
          ? typeof anyErr.response?.data === "string"
            ? anyErr.response?.data
            : JSON.stringify(anyErr.response?.data)
          : anyErr.message ?? "Save failed";
      setEditPromoError(String(errBody));
    }
  };

  // Project the zone/override/store composite display string onto each
  // row so the grid can render it as a single column. Mirrors legacy
  // Item/ViewItem.aspx formatting — see audit 14-parity §5 item 33.
  const allowanceRows = useMemo(
    () =>
      (allowances.data ?? []).map((a: DealItemAllowance) => ({
        ...a,
        zoneStoreDisplay: formatCostZoneStore({ costZone: a.costZoneNumber, store: a.storeNumber }),
      })),
    [allowances.data]
  );
  const scanRows = useMemo(
    () =>
      (scans.data ?? []).map((sc: DealItemScan) => ({
        ...sc,
        zoneStoreDisplay: formatZoneOverrideStore({
          zone: sc.zoneNumber,
          override: sc.overrideNumber,
          store: sc.storeNumber,
        }),
      })),
    [scans.data]
  );
  const promoPriceRows = useMemo(
    () =>
      (promoPrices.data ?? []).map((p: DealItemPromoPrice) => ({
        ...p,
        zoneStoreDisplay: formatZoneOverrideStore({
          zone: p.zoneNumber,
          override: p.overrideNumber,
          store: p.storeNumber,
        }),
      })),
    [promoPrices.data]
  );
  const marginRows = useMemo(
    () =>
      (margins.data ?? []).map((m: DealItemMargin) => ({
        ...m,
        zoneStoreDisplay: formatZoneOverrideStore({
          zone: m.zoneNumber,
          store: m.storeNumber,
        }),
      })),
    [margins.data]
  );

  if (!dealItemId || Number.isNaN(dealItemId)) return <p>Invalid item id.</p>;

  const runRecalc = async () => {
    if (!deal.dealId) return;
    setRecalcPending(true);
    setRecalcMsg("");
    try {
      const url = `${APIS.Deals}/${deal.dealId}/items/${dealItemId}/recalc-margins`;
      const response = await apiClient.post(url);
      setRecalcMsg(typeof response.data === "string" ? response.data : JSON.stringify(response.data));
    } catch (err: unknown) {
      // 501 stub returns a structured body — surface it verbatim.
      // axios stuffs the server payload onto err.response.data
      const anyErr = err as { response?: { status?: number; data?: unknown }; message?: string };
      const status = anyErr.response?.status ?? "?";
      const body =
        anyErr.response?.data !== undefined
          ? typeof anyErr.response?.data === "string"
            ? anyErr.response?.data
            : JSON.stringify(anyErr.response?.data, null, 2)
          : anyErr.message ?? "Recalc failed";
      setRecalcMsg(`[HTTP ${status}] ${body}`);
    } finally {
      setRecalcPending(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
        <Link
          to={`/dm/deals/${deal.dealId}/items`}
          style={{ fontSize: "0.85rem", color: "#0a3d62", textDecoration: "none" }}
        >
          ← Back to items
        </Link>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            type="button"
            size="small"
            fillMode="outline"
            disabled={prevId === undefined}
            onClick={() => prevId !== undefined && navigate(`/dm/deals/${deal.dealId}/items/${prevId}`)}
          >
            ← Previous
          </Button>
          <Button
            type="button"
            size="small"
            fillMode="outline"
            disabled={nextId === undefined}
            onClick={() => nextId !== undefined && navigate(`/dm/deals/${deal.dealId}/items/${nextId}`)}
          >
            Next →
          </Button>
        </div>
      </div>
      <h2 style={{ marginTop: "0.5rem" }}>
        Item {dealItemId}
        {item.data && (
          <span style={{ color: "#64748b", fontWeight: 400, marginLeft: "0.75rem", fontSize: "1rem" }}>
            · retail #{item.data.retailItemNumber}
            {item.data.familyCode != null && <> · family {item.data.familyCode}</>}
          </span>
        )}
      </h2>

      {item.isLoading && <p>Loading item…</p>}
      {item.isError && (
        <p style={{ color: "#b91c1c" }}>
          {item.error instanceof Error ? item.error.message : "Failed to load"}
        </p>
      )}

      <TabStrip selected={tab} onSelect={(e) => setTab(e.selected)}>
        <TabStripTab title="Summary">
          {item.data && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem 1.25rem", padding: "0.5rem 0" }}>
              <SummaryField label="Retail Item #" value={item.data.retailItemNumber} />
              <SummaryField label="Family code" value={item.data.familyCode ?? "—"} />
              <SummaryField label="Price group" value={item.data.priceGroupCode} />
              <SummaryField label="Supplier #" value={item.data.supplierNumber ?? "—"} />
              <SummaryField
                label="Description"
                value={retail.data?.itemDescription ?? (retail.isLoading ? "Loading…" : "—")}
              />
              <SummaryField label="Item UPC" value={retail.data?.upc ?? "—"} />
              <SummaryField label="Case UPC" value={retail.data?.caseUpc ?? "—"} />
              <SummaryField label="Whitem # (cd)" value={retail.data?.whitemNumberWithCheckDigit ?? "—"} />
              <SummaryField label="Pack" value={retail.data?.itemPack ?? "—"} />
              <SummaryField
                label="Size"
                value={
                  retail.data
                    ? `${retail.data.itemSize ?? ""} ${retail.data.itemSizeUom ?? ""}`.trim() || "—"
                    : "—"
                }
              />
              <SummaryField
                label="Vendor"
                value={
                  retail.data
                    ? `${retail.data.vendorNumber ?? ""}${retail.data.vendorName ? ` — ${retail.data.vendorName}` : ""}` || "—"
                    : "—"
                }
              />
              <SummaryField label="AP vendor #" value={retail.data?.apVendorNumber ?? "—"} />
              <SummaryField
                label="Projected units"
                value={
                  item.data.projectedUnits != null
                    ? `${item.data.projectedUnits} ${item.data.projectedUnitsUom ?? ""}`.trim()
                    : "—"
                }
              />
              <SummaryField label="Warehouse" value={item.data.warehouseFacilityNumber ?? "—"} />
              <SummaryField label="Reward type" value={item.data.rewardItemTypeCode ?? "—"} />
              <SummaryField label="Item status" value={retail.data?.itemStatus ?? "—"} />
            </div>
          )}

          {(item.data?.shipperFirstOrderDate ||
            item.data?.shipperLastOrderDate ||
            item.data?.shipperFirstShipDate ||
            item.data?.shipperLastShipDate) && (
            <div style={{ marginTop: "0.75rem", padding: "0.75rem", border: "1px solid #e2e8f0", borderRadius: 6, background: "#f8fafc" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "#334155" }}>Shipper dates</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem 1rem" }}>
                <SummaryField label="First order" value={fmtDate(item.data?.shipperFirstOrderDate)} />
                <SummaryField label="Last order" value={fmtDate(item.data?.shipperLastOrderDate)} />
                <SummaryField label="First ship" value={fmtDate(item.data?.shipperFirstShipDate)} />
                <SummaryField label="Last ship" value={fmtDate(item.data?.shipperLastShipDate)} />
              </div>
            </div>
          )}
        </TabStripTab>

        <TabStripTab title={`Allowances (${allowances.data?.length ?? 0})`}>
          {allowances.isLoading && <p>Loading…</p>}
          {allowanceRows.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>No allowances on this item.</p>
          ) : (
            <Grid data={allowanceRows}>
              <GridColumn field="allowanceId" title="#" width="60px" />
              <GridColumn field="zoneStoreDisplay" title="Zone / Store" width="180px" />
              <GridColumn field="offInvoiceAmount" title="Off Invoice" width="120px" format="{0:c4}" />
              <GridColumn field="billBackAmount" title="Bill Back" width="120px" format="{0:c4}" />
              <GridColumn field="countRecountAmount" title="Count/Recount" width="120px" format="{0:c4}" />
              <GridColumn field="passThruAmount" title="Pass Thru" width="120px" format="{0:c4}" />
              <GridColumn field="reflectAmount" title="Reflect" width="120px" format="{0:c4}" />
              <GridColumn field="insideCostMargin" title="Inside Cost Mgn" width="140px" format="{0:n4}" />
            </Grid>
          )}
        </TabStripTab>

        <TabStripTab title={`Scans (${scans.data?.length ?? 0})`}>
          {scans.isLoading && <p>Loading…</p>}
          {scanRows.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>No scans on this item.</p>
          ) : (
            <Grid data={scanRows}>
              <GridColumn field="scanId" title="#" width="60px" />
              <GridColumn field="zoneStoreDisplay" title="Zone / Override / Store" width="220px" />
              <GridColumn field="amount" title="Amount" width="120px" format="{0:c4}" />
              <GridColumn field="internalVendorCode" title="Vendor" width="80px" />
              <GridColumn field="movementCode" title="Movement" width="100px" />
              <GridColumn field="businessUnit" title="BU" width="80px" />
              <GridColumn field="apVendorNumber" title="AP Vendor" width="120px" />
              <GridColumn field="invoiceDescription" title="Invoice Description" />
            </Grid>
          )}
        </TabStripTab>

        <TabStripTab title={`Promo Prices (${promoPrices.data?.length ?? 0})`}>
          {promoPrices.isLoading && <p>Loading…</p>}
          <div
            style={{
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              padding: "0.4rem 0.75rem",
              borderRadius: 4,
              fontSize: "0.78rem",
              color: "#334155",
              margin: "0.5rem 0",
            }}
          >
            Each row must use <strong>exactly one</strong> of: Price (with
            M-Unit), Amount Off, or % Off — the Oracle{" "}
            <code>DIPP_BEF_IU</code> trigger rejects rows with multiple or
            zero set. Click <em>Edit</em> on a row to change its values.
          </div>
          {editPromoError && (
            <pre
              style={{
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                color: "#991b1b",
                padding: "0.5rem 0.75rem",
                borderRadius: 4,
                fontSize: "0.78rem",
                whiteSpace: "pre-wrap",
                margin: "0.5rem 0",
              }}
            >
              {editPromoError}
            </pre>
          )}
          {promoPriceRows.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>No promo prices on this item.</p>
          ) : (
            <Grid data={promoPriceRows}>
              <GridColumn field="promoPriceId" title="#" width="60px" />
              <GridColumn field="zoneStoreDisplay" title="Zone / Override / Store" width="220px" />
              <GridColumn
                field="multiUnitCount"
                title="M-Unit"
                width="90px"
                cells={{
                  data: (props: GridCellProps) => {
                    const row = props.dataItem as DealItemPromoPrice;
                    if (editPromoPriceId === row.promoPriceId) {
                      return (
                        <td>
                          <NumericTextBox
                            value={editPromoForm.multiUnitCount}
                            spinners={false}
                            format="#"
                            min={0}
                            onChange={(e) =>
                              setEditPromoForm((p) => ({ ...p, multiUnitCount: e.value ?? null }))
                            }
                          />
                        </td>
                      );
                    }
                    return <td>{row.multiUnitCount ?? ""}</td>;
                  },
                }}
              />
              <GridColumn
                field="price"
                title="Price"
                width="120px"
                cells={{
                  data: (props: GridCellProps) => {
                    const row = props.dataItem as DealItemPromoPrice;
                    if (editPromoPriceId === row.promoPriceId) {
                      return (
                        <td>
                          <NumericTextBox
                            value={editPromoForm.price}
                            spinners={false}
                            format="c2"
                            min={0}
                            onChange={(e) =>
                              setEditPromoForm((p) => ({ ...p, price: e.value ?? null }))
                            }
                          />
                        </td>
                      );
                    }
                    return (
                      <td style={{ textAlign: "right" }}>
                        {row.price != null ? `$${row.price.toFixed(2)}` : ""}
                      </td>
                    );
                  },
                }}
              />
              <GridColumn
                field="amountOff"
                title="Amt Off"
                width="120px"
                cells={{
                  data: (props: GridCellProps) => {
                    const row = props.dataItem as DealItemPromoPrice;
                    if (editPromoPriceId === row.promoPriceId) {
                      return (
                        <td>
                          <NumericTextBox
                            value={editPromoForm.amountOff}
                            spinners={false}
                            format="c2"
                            min={0}
                            onChange={(e) =>
                              setEditPromoForm((p) => ({ ...p, amountOff: e.value ?? null }))
                            }
                          />
                        </td>
                      );
                    }
                    return (
                      <td style={{ textAlign: "right" }}>
                        {row.amountOff != null ? `$${row.amountOff.toFixed(2)}` : ""}
                      </td>
                    );
                  },
                }}
              />
              <GridColumn
                field="percentOff"
                title="% Off"
                width="100px"
                cells={{
                  data: (props: GridCellProps) => {
                    const row = props.dataItem as DealItemPromoPrice;
                    if (editPromoPriceId === row.promoPriceId) {
                      return (
                        <td>
                          <NumericTextBox
                            value={editPromoForm.percentOff}
                            spinners={false}
                            format="n2"
                            min={0}
                            max={100}
                            onChange={(e) =>
                              setEditPromoForm((p) => ({ ...p, percentOff: e.value ?? null }))
                            }
                          />
                        </td>
                      );
                    }
                    return (
                      <td style={{ textAlign: "right" }}>
                        {row.percentOff != null ? row.percentOff.toFixed(2) : ""}
                      </td>
                    );
                  },
                }}
              />
              <GridColumn field="specialPricing" title="Special" width="100px" />
              <GridColumn field="regularRetailMarginBeforeScan" title="Reg Mgn" width="100px" format="{0:n4}" />
              <GridColumn field="promoRetailMarginBeforeScan" title="Promo Mgn (b)" width="120px" format="{0:n4}" />
              <GridColumn field="promoRetailMarginAfterScan" title="Promo Mgn (a)" width="120px" format="{0:n4}" />
              <GridColumn
                title="Actions"
                width="160px"
                cells={{
                  data: (props: GridCellProps) => {
                    const row = props.dataItem as DealItemPromoPrice;
                    const isEditing = editPromoPriceId === row.promoPriceId;
                    if (isEditing) {
                      return (
                        <td>
                          <Button
                            size="small"
                            themeColor="primary"
                            disabled={updatePromoPrice.isPending}
                            onClick={() => saveEditPromoPrice(row)}
                          >
                            {updatePromoPrice.isPending ? "Saving…" : "Save"}
                          </Button>{" "}
                          <Button
                            size="small"
                            fillMode="flat"
                            disabled={updatePromoPrice.isPending}
                            onClick={cancelEditPromoPrice}
                          >
                            Cancel
                          </Button>
                        </td>
                      );
                    }
                    return (
                      <td>
                        <Button
                          size="small"
                          fillMode="outline"
                          disabled={editPromoPriceId !== null}
                          onClick={() => beginEditPromoPrice(row)}
                          title="Edit price, amount-off, and percent-off for this row"
                        >
                          Edit
                        </Button>
                      </td>
                    );
                  },
                }}
              />
            </Grid>
          )}
        </TabStripTab>

        <TabStripTab title={`Margins (${margins.data?.length ?? 0})`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0.5rem 0" }}>
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Margins are populated by the Oracle <code>dm0039_margin_calc</code> package.
            </div>
            <Button
              type="button"
              size="small"
              themeColor="primary"
              fillMode="outline"
              disabled={recalcPending}
              onClick={runRecalc}
            >
              {recalcPending ? "Recalculating…" : "Recalc margins"}
            </Button>
          </div>
          {recalcMsg && (
            <pre
              style={{
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                padding: "0.5rem 0.75rem",
                borderRadius: 4,
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                color: "#334155",
              }}
            >
              {recalcMsg}
            </pre>
          )}
          {margins.isLoading && <p>Loading…</p>}
          {marginRows.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>
              No margin calculations available. The legacy app refreshes these
              via the dm0039_margin_calc PL/SQL package; we haven't wired that
              call yet — use "Recalc margins" above to invoke the (stub)
              endpoint.
            </p>
          ) : (
            <Grid data={marginRows}>
              <GridColumn field="costZoneNumber" title="Cost Z" width="80px" />
              <GridColumn field="zoneStoreDisplay" title="Zone / Store" width="180px" />
              <GridColumn field="regularPrice" title="Reg Price" width="100px" format="{0:c2}" />
              <GridColumn field="currentPrice" title="Curr Price" width="100px" format="{0:c2}" />
              <GridColumn field="dealPrice" title="Deal Price" width="100px" format="{0:c2}" />
              <GridColumn field="saveAmount" title="Save" width="100px" format="{0:c2}" />
              <GridColumn field="currentRetailMarginBeforeScan" title="Curr Mgn" width="100px" format="{0:n4}" />
              <GridColumn field="promoRetailMarginBeforeScan" title="Promo Mgn (b)" width="120px" format="{0:n4}" />
              <GridColumn field="promoRetailMarginAfterScan" title="Promo Mgn (a)" width="120px" format="{0:n4}" />
              <GridColumn field="insideCostMargin" title="Inside Cst" width="100px" format="{0:n4}" />
              <GridColumn field="insideDealCostMargin" title="Inside Deal" width="100px" format="{0:n4}" />
              <GridColumn field="calcErrorDescription" title="Calc Error" />
            </Grid>
          )}
        </TabStripTab>
      </TabStrip>
    </div>
  );
};

export default DealItemDetailPage;
